<template>
  <div class="container-fluid py-1">
    <div class="row">
      <div class="col-12">
        <div class="card my-4">
          <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
            <div
              class="bg-gradient-success shadow-success border-radius-lg pt-2 pb-3"
            >
              <div
                class="d-flex justify-content-between align-items-center px-3"
              >
                <div>
                  <h6 class="text-white text-capitalize mb-1">
                    Profile Management
                  </h6>
                  <p class="text-white text-sm mb-0 opacity-8">
                    Manage your personal and company information
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div class="card-body px-0 pb-2">
            <div class="px-4 pb-3">
              <div class="row">
                <div class="col-lg-6">
                  <div class="card mb-4">
                    <div class="card-header pb-0">
                      <div
                        class="d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <h6 class="mb-0">Personal Information</h6>
                        </div>
                      </div>
                    </div>
                    <div class="card-body pt-3">
                      <div class="row">
                        <div class="col-12 mb-3">
                          <label class="form-label text-sm font-weight-bold">
                            Full Name
                          </label>
                          <div class="form-control-view">
                            {{ userData.full_name || "Not specified" }}
                          </div>
                        </div>

                        <div class="col-12 mb-3">
                          <label class="form-label text-sm font-weight-bold">
                            Email Address
                          </label>
                          <div class="form-control-view">
                            <i class="material-icons text-sm me-2">email</i>
                            {{ userData.email || "Not specified" }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="card mb-4">
                    <div class="card-header pb-0">
                      <div
                        class="d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <h6 class="mb-0">
                            <i class="material-icons text-sm me-1">lock</i>
                            Change Password
                          </h6>
                        </div>
                        <button
                          v-if="!isChangingPassword"
                          class="btn btn-sm btn-warning"
                          @click="startChangePassword"
                        >
                          <i class="material-icons text-sm me-1">vpn_key</i>
                          Change
                        </button>
                        <div v-else class="btn-group">
                          <button
                            class="btn btn-sm btn-success"
                            @click="savePassword"
                          >
                            <i class="material-icons text-sm me-1">save</i> Save
                          </button>
                          <button
                            class="btn btn-sm btn-secondary"
                            @click="cancelChangePassword"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                    <div class="card-body pt-3">
                      <div v-if="isChangingPassword">
                        <div class="mb-3">
                          <label class="form-label text-sm font-weight-bold">
                            Current Password
                          </label>
                          <div class="input-group">
                            <input
                              :type="showCurrentPassword ? 'text' : 'password'"
                              class="form-control"
                              v-model="passwordData.current_password"
                              placeholder="Enter current password"
                            />
                            <button
                              class="btn btn-outline-secondary"
                              type="button"
                              @click="
                                showCurrentPassword = !showCurrentPassword
                              "
                            >
                              <i class="material-icons">
                                {{
                                  showCurrentPassword
                                    ? "visibility_off"
                                    : "visibility"
                                }}
                              </i>
                            </button>
                          </div>
                        </div>

                        <div class="mb-3">
                          <label class="form-label text-sm font-weight-bold">
                            New Password
                          </label>
                          <div class="input-group">
                            <input
                              :type="showNewPassword ? 'text' : 'password'"
                              class="form-control"
                              v-model="passwordData.new_password"
                              placeholder="Enter new password"
                            />
                            <button
                              class="btn btn-outline-secondary"
                              type="button"
                              @click="showNewPassword = !showNewPassword"
                            >
                              <i class="material-icons">
                                {{
                                  showNewPassword
                                    ? "visibility_off"
                                    : "visibility"
                                }}
                              </i>
                            </button>
                          </div>
                          <div class="form-text text-muted text-xs mt-1">
                            Password must be at least 6 characters long
                          </div>
                        </div>

                        <div class="mb-3">
                          <label class="form-label text-sm font-weight-bold">
                            Confirm New Password
                          </label>
                          <div class="input-group">
                            <input
                              :type="showConfirmPassword ? 'text' : 'password'"
                              class="form-control"
                              v-model="passwordData.confirm_password"
                              placeholder="Confirm new password"
                            />
                            <button
                              class="btn btn-outline-secondary"
                              type="button"
                              @click="
                                showConfirmPassword = !showConfirmPassword
                              "
                            >
                              <i class="material-icons">
                                {{
                                  showConfirmPassword
                                    ? "visibility_off"
                                    : "visibility"
                                }}
                              </i>
                            </button>
                          </div>
                        </div>
                      </div>
                      <div v-else class="text-center py-4">
                        <p class="text-muted mb-0">
                          Click the Change button to update your password
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-lg-6">
                  <div class="card mb-4">
                    <div class="card-header pb-0">
                      <div
                        class="d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <h6 class="mb-0">Company Information</h6>
                        </div>
                        <button
                          v-if="!isEditingCompany && hasEditPermission"
                          class="btn btn-sm btn-primary"
                          @click="startEditCompany"
                        >
                          <i class="material-icons text-sm me-1">edit</i> Edit
                        </button>
                        <div
                          v-else-if="isEditingCompany && hasEditPermission"
                          class="btn-group"
                        >
                          <button
                            class="btn btn-sm btn-success"
                            @click="saveCompanyInfo"
                          >
                            <i class="material-icons text-sm me-1">save</i> Save
                          </button>
                          <button
                            class="btn btn-sm btn-secondary"
                            @click="cancelEditCompany"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                    <div class="card-body pt-3">
                      <div
                        v-if="!hasEditPermission"
                        class="alert alert-warning mb-4"
                      >
                        <i class="material-icons text-sm me-2">info</i>
                        <span class="text-sm">
                          Only <strong>Administrators</strong> can edit company
                          information.
                        </span>
                      </div>

                      <div class="row">
                        <div class="col-12 mb-3">
                          <label class="form-label text-sm font-weight-bold">
                            Company Name
                          </label>
                          <div
                            v-if="!isEditingCompany"
                            class="form-control-view"
                          >
                            <i class="material-icons text-sm me-2">business</i>
                            {{ companyData.operator_name || "Not specified" }}
                          </div>
                          <input
                            v-else
                            type="text"
                            class="form-control"
                            v-model="editableCompany.operator_name"
                            placeholder="Enter company name"
                          />
                        </div>

                        <div class="col-md-6 mb-3">
                          <label class="form-label text-sm font-weight-bold"
                            >Operator ID</label
                          >
                          <div class="form-control-view">
                            {{ companyData.operator_id || "Not specified" }}
                          </div>
                        </div>

                        <div class="col-12 mb-3">
                          <label class="form-label text-sm font-weight-bold">
                            Company Email
                          </label>
                          <div
                            v-if="!isEditingCompany"
                            class="form-control-view"
                          >
                            <i class="material-icons text-sm me-2">email</i>
                            {{ companyData.operator_email || "Not specified" }}
                          </div>
                          <input
                            v-else
                            type="email"
                            class="form-control"
                            v-model="editableCompany.operator_email"
                            placeholder="Enter company email"
                          />
                        </div>

                        <div class="col-md-12 mb-3">
                          <label class="form-label text-sm font-weight-bold"
                            >Contact Number</label
                          >
                          <div
                            v-if="!isEditingCompany"
                            class="form-control-view"
                          >
                            <i class="material-icons text-sm me-2">phone</i>
                            {{ companyData.contact || "Not specified" }}
                          </div>
                          <input
                            v-else
                            type="tel"
                            class="form-control"
                            v-model="editableCompany.contact"
                            placeholder="Enter contact number"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showToast"
      class="position-fixed bottom-1 end-1"
      style="z-index: 1050"
    >
      <div class="toast fade show" role="alert">
        <div
          class="toast-header"
          :class="
            toastType === 'success'
              ? 'bg-success text-white'
              : 'bg-danger text-white'
          "
        >
          <i class="material-icons me-2">{{
            toastType === "success" ? "check_circle" : "error"
          }}</i>
          <strong class="me-auto">{{
            toastType === "success" ? "Success" : "Error"
          }}</strong>
          <button
            type="button"
            class="btn-close btn-close-white"
            @click="showToast = false"
          ></button>
        </div>
        <div class="toast-body">{{ toastMessage }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "ProfilePage",
  data() {
    return {
      isChangingPassword: false,
      isEditingCompany: false,
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      userData: {
        user_id: "",
        full_name: "",
        email: "",
        operator_email: "",
        role: "",
        status: "",
      },
      passwordData: {
        current_password: "",
        new_password: "",
        confirm_password: "",
      },
      companyData: {
        id: "",
        operator_name: "",
        operator_id: "",
        operator_email: "",
        contact: "",
        operator: "",
      },
      editableCompany: {
        id: "",
        operator_name: "",
        operator_id: "",
        operator_email: "",
        contact: "",
        operator: "",
      },
      showToast: false,
      toastMessage: "",
      toastType: "success",
    };
  },
  created() {
    this.loadUserData();
    window.addEventListener("storage", this.handleStorageChange);
  },
  beforeUnmount() {
    window.removeEventListener("storage", this.handleStorageChange);
  },
  methods: {
    loadUserData() {
      try {
        const storedUser = localStorage.getItem("user");
        if (storedUser && storedUser !== "undefined" && storedUser !== "null") {
          const user = JSON.parse(storedUser);

          this.userData = {
            user_id: user.user_id || "",
            full_name: user.full_name || "",
            email: user.email || "",
            role: user.role || "",
            status: user.status || "",
          };

          this.companyData = {
            id: user.id || "",
            operator_name: user.operator_name || "",
            operator_id: user.operator_id || "",
            operator_email: user.operator_email || user.email || "",
            contact: user.contact || "",
            operator: user.operator || "",
          };

          this.editableCompany = { ...this.companyData };
        } else {
          this.showError("No user data found. Please log in again.");
        }
      } catch (error) {
        this.showError("Error loading profile data.");
      }
    },

    handleStorageChange(event) {
      if (event.key === "user") {
        this.loadUserData();
      }
    },

    startChangePassword() {
      this.isChangingPassword = true;
      this.passwordData = {
        current_password: "",
        new_password: "",
        confirm_password: "",
      };
      this.showCurrentPassword = false;
      this.showNewPassword = false;
      this.showConfirmPassword = false;
    },

    cancelChangePassword() {
      this.isChangingPassword = false;
      this.passwordData = {
        current_password: "",
        new_password: "",
        confirm_password: "",
      };
    },

    async savePassword() {
      try {
        if (!this.passwordData.current_password) {
          this.showError("Current password is required.");
          return;
        }

        if (!this.passwordData.new_password) {
          this.showError("New password is required.");
          return;
        }

        if (!this.passwordData.confirm_password) {
          this.showError("Please confirm your new password.");
          return;
        }

        if (
          this.passwordData.new_password !== this.passwordData.confirm_password
        ) {
          this.showError("New passwords do not match.");
          return;
        }

        if (this.passwordData.new_password.length < 6) {
          this.showError("New password must be at least 6 characters long.");
          return;
        }

        const passwordChangeData = {
          user_id: this.userData.user_id,
          current_password: this.passwordData.current_password,
          new_password: this.passwordData.new_password,
        };

        const response = await axios.post(
          "/change-password/",
          passwordChangeData
        );

        if (response.data.success) {
          this.isChangingPassword = false;
          this.passwordData = {
            current_password: "",
            new_password: "",
            confirm_password: "",
          };
          this.showSuccess("Password changed successfully!");
        } else {
          this.showError(response.data.message || "Failed to change password.");
        }
      } catch (error) {
        if (error.response?.data?.message) {
          this.showError(error.response.data.message);
        } else {
          this.showError("Failed to change password.");
        }
      }
    },

    startEditCompany() {
      if (this.hasEditPermission) {
        this.isEditingCompany = true;
        this.editableCompany = { ...this.companyData };
      } else {
        this.showError(
          "You do not have permission to edit company information."
        );
      }
    },

    cancelEditCompany() {
      this.isEditingCompany = false;
      this.editableCompany = { ...this.companyData };
    },
    async saveCompanyInfo() {
      try {
        const data = {
          operator_name: this.editableCompany.operator_name,
          operator_email: this.editableCompany.operator_email,
          contact: this.editableCompany.contact,
        };

        await axios.patch(`/operators/${this.editableCompany.id}/`, data);

        const user = JSON.parse(localStorage.getItem("user"));
        localStorage.setItem("user", JSON.stringify({ ...user, ...data }));

        this.companyData = { ...this.editableCompany };
        this.isEditingCompany = false;
        this.showSuccess("Saved!");
      } catch {
        this.showError(" Failed to edit Company Info.");
      }
    },
    showSuccess(message) {
      this.toastMessage = message;
      this.toastType = "success";
      this.showToast = true;
      setTimeout(() => {
        this.showToast = false;
      }, 3000);
    },

    showError(message) {
      this.toastMessage = message;
      this.toastType = "error";
      this.showToast = true;
      setTimeout(() => {
        this.showToast = false;
      }, 3000);
    },
  },
  computed: {
    userRole() {
      return parseInt(this.userData.role) || 3;
    },
    hasEditPermission() {
      return this.userRole === 1;
    },
  },
};
</script>

<style scoped>
.form-control-view {
  min-height: 38px;
  padding: 0.375rem 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
}

.card {
  border: none;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  border-radius: 0.75rem;
}

.card-header {
  background-color: rgba(255, 255, 255, 0.95);
  border-bottom: 1px solid #dee2e6;
  padding: 1rem 1.25rem;
  border-radius: 0.75rem 0.75rem 0 0 !important;
}

.bg-gradient-success {
  background: linear-gradient(310deg, #f44335 0%, #ef5350 100%);
}

.shadow-success {
  box-shadow:
    0 4px 20px 0 rgba(0, 0, 0, 0.14),
    0 7px 10px -5px rgba(76, 175, 80, 0.4);
}

.border-radius-lg {
  border-radius: 0.75rem;
}

.form-control {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  height: 38px;
}

.form-control:focus {
  border-color: #4caf50;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
}

.input-group .form-control {
  border-right: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.input-group .btn-outline-secondary {
  border: 1px solid #dee2e6;
  border-left: 0;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.75rem;
}

.input-group .btn-outline-secondary:hover {
  background-color: #f8f9fa;
}

.toast {
  min-width: 300px;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.form-text {
  font-size: 0.75rem;
  color: #6c757d;
  margin-top: 0.25rem;
}

@media (max-width: 768px) {
  .btn-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .toast {
    min-width: 250px;
  }
}
</style>
